import React, { useEffect, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppConfig } from '../../context/app-configs-context';
import { DynamicFormProps } from './dynamicFormTypes';
import { buildZod, computeActiveFields } from './formsUtils';
import DynamicFormSkin1 from './components/DynamicFormSkin1';
import DynamicFormSkin2 from './components/DynamicFormSkin2';
import DynamicFormSkin5 from './components/DynamicFormSkin5';
import './DynamicForm.scss';

type DynamicFormPropsWithAdditionalContent = DynamicFormProps & {
    additionalTopContent?: React.ReactNode;
};

const DynamicForm: React.FC<DynamicFormPropsWithAdditionalContent> = ({
    formId,
    formName,
    formType,
    config,
    onSubmit,
    apiSubmitting = false,
    saveButtonLabel,
    hasCancelButton = false,
    onCancel,
    skin = 'skin2',
    shouldSeparateLeaves = false,
    resetFormEmittedData,
    additionalTopContent,
}) => {
    const { appConfigs: { show: { formTitle, rightTitle, copyLabel, copyButton, insert, cancel } } } = useAppConfig();
    const [ isSubmitted, setIsSubmitted ] = useState<boolean>(false);

    const defaults = useMemo(() => {
        const d: Record<string, any> = {};

        config.fields.forEach((f) => {
            if (f.defaultValue !== undefined) {
                d[f.id] = f.defaultValue;
            } else if (f.type === 'date') {
                d[f.id] = null;
            } else if (f.type === 'color') {
                d[f.id] = '#000000';
            } else {
                d[f.id] = '';
            }
        });

        return d;
    }, [ config.fields ]);

    const [ valuesState, setValuesState ] = useState<Record<string, any>>(defaults);

    const { activeFields, activeLeaves } = useMemo(
        () => computeActiveFields(config.fields, valuesState, shouldSeparateLeaves),
        [ config.fields, valuesState, shouldSeparateLeaves ],
    );

    const schema = useMemo(() => buildZod(activeFields), [ activeFields ]);
    const activeKey = useMemo(() => activeFields.map((f) => f.id).join('|'), [ activeFields ]);

    useEffect(() => {
        setValuesState(defaults);
    }, [ defaults ]);

    useEffect(() => {
        const activeNames = new Set(activeFields.map((f) => f.id));
        let changed = false;
        const next = { ...valuesState };

        for (const f of config.fields) {
            if (!activeNames.has(f.id)) {
                const target = defaults[f.id];

                if (next[f.id] !== target) {
                    next[f.id] = target;
                    changed = true;
                }
            }
        }

        if (changed) {
            setValuesState(next);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ activeFields, config.fields, defaults ]);

    const onSubmitWrapper = async (values: any) => {
        await onSubmit(values);
        setIsSubmitted(true);
    };

    const onHideLeaves = () => {
        setIsSubmitted(false);
        resetFormEmittedData?.();
    };

    const shouldHideForm =
        !additionalTopContent &&
        (activeFields.length === 0 || activeFields.every((field) => field.hidden)) &&
        activeLeaves.length === 0;

    if (shouldHideForm) {
        return null;
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="dynamic_form_container">
                {skin === 'skin1' ? (
                    <div className="dynamic_form_inside_box">
                        <DynamicFormSkin1
                            key={activeKey}
                            fields={activeFields}
                            schema={schema}
                            defaults={valuesState}
                            onSubmit={onSubmitWrapper}
                            onValuesChange={setValuesState}
                            apiSubmitting={apiSubmitting}
                            saveButtonLabel={saveButtonLabel}
                            hasCancelButton={hasCancelButton}
                            onCancel={onCancel}
                            skin={skin}
                            formId={formId}
                            formName={formName}
                            isSubmitted={isSubmitted}
                            shouldSeparateLeaves={shouldSeparateLeaves}
                            activeLeaves={activeLeaves}
                        />
                    </div>
                ) : [ 'skin2', 'skin3', 'skin4' ].includes(skin) ? (
                    <DynamicFormSkin2
                        key={activeKey}
                        fields={activeFields}
                        schema={schema}
                        defaults={valuesState}
                        onSubmit={onSubmitWrapper}
                        onValuesChange={setValuesState}
                        apiSubmitting={apiSubmitting}
                        saveButtonLabel={saveButtonLabel}
                        hasCancelButton={hasCancelButton}
                        hasResetButton={skin === 'skin3'}
                        onCancel={onCancel}
                        skin={skin}
                        formId={formId}
                        formName={formName}
                        formType={formType}
                        onHideLeaves={onHideLeaves}
                        shouldSeparateLeaves={shouldSeparateLeaves}
                        activeLeaves={activeLeaves}
                        isSubmitted={isSubmitted}
                        formTitle={formTitle}
                        rightTitle={rightTitle}
                        insetLabel={insert}
                        cancelLabel={cancel}
                        copyLabel={copyLabel}
                        hasCopyButton={copyButton}
                        additionalTopContent={additionalTopContent}
                    />
                ) : skin === 'skin5' ? (
                    <DynamicFormSkin5
                        key={activeKey}
                        fields={activeFields}
                        schema={schema}
                        defaults={valuesState}
                        onSubmit={onSubmitWrapper}
                        onValuesChange={setValuesState}
                        apiSubmitting={apiSubmitting}
                        saveButtonLabel={saveButtonLabel}
                        hasCancelButton={hasCancelButton}
                        onCancel={onCancel}
                        skin={skin}
                        formId={formId}
                        formName={formName}
                        isSubmitted={isSubmitted}
                        shouldSeparateLeaves={shouldSeparateLeaves}
                        activeLeaves={activeLeaves}
                    />
                ) : (
                    <div>Skin is coming soon</div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default DynamicForm;
